import apiRequest from "../utils/ApiRequest/apiRequest";
import React, { useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import ManageMfaModal from "../components/Pages/ProfileUser/Profile/MfaSecurity/modals/ManageMfaModal";
import useReduxUser from "./useReduxUser";

import { useUser } from "contexts/userContext/userContext";
import { useContent } from "contexts/contentContext/contentContext";
import { useModal } from "contexts/modalContext/modalContext";
import { useToasts } from "react-toast-notifications";
import { useState } from "react";

import styles from "../components/Pages/ProfileUser/Profile/MfaSecurity/styles.module.scss";

const RESEND_AWAIT_TIME_S = 60;
const TICK_INTERVAL_MS = 1000;

const useMfa = () => {
  const { addToast } = useToasts();
  const { closeModal, setModal } = useModal();
  const { getUserData } = useUser();
  const { updateSettings } = useReduxUser();
  const { getLanguageCulture, getBasicSettingsData, getTextByKey } =
    useContent();

  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [isMailSent, setIsMailSent] = useState(false);
  const [timLeft, setTimeLeft] = useState(-1);
  const timerRef = React.useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (timLeft === 0) {
      clearInterval(timerRef.current as NodeJS.Timeout);
      setIsResendEnabled(true);
      setTimeLeft(-1);
    }
  }, [timLeft]);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current as NodeJS.Timeout);
      setTimeLeft(-1);
    };
  }, []);

  // func to generate random session id
  const generateSessionId = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  const startTimer = () => {
    setIsResendEnabled(false);
    setTimeLeft(RESEND_AWAIT_TIME_S);

    clearInterval(timerRef.current as NodeJS.Timeout);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, TICK_INTERVAL_MS);
  };

  const sendMfaCodeOnEmail = async (
    captchaToken: string,
    sessionId: string,
    email?: string
  ) => {
    setIsLoading(true);
    const userData = getUserData();
    const userEmail = userData?.UserName;

    let token = captchaToken;
    // try {
    //   recaptchaRef.current?.reset();
    //   token = getBasicSettingsData().recaptchaKeyReact
    //     ? await (recaptchaRef.current as ReCAPTCHA).executeAsync()
    //     : true;
    // } catch (err) {
    //   addToast(getTextByKey("recaptcha-token-cant-execute"), {
    //     appearance: "error",
    //   });
    //   token = false;
    // }

    if (!token) {
      setIsLoading(false);
      return;
    }

    if (userEmail || email) {
      // Send MFA code on email
      const res = await apiRequest(
        "UserVerifiedCode",
        "CreateMFANewVerifiedCode",
        {
          session_id: sessionId,
          sender_email_address: userEmail || email,
          culture: getLanguageCulture(),
          isV3Recaptcha: true,
          recaptchaResponse: token,
        },
        "POST"
      );

      setIsLoading(false);

      if (res.status === "success") {
        addToast(getTextByKey("mfa-code-sent-on-email"), {
          appearance: "success",
        });
        setIsMailSent(true);

        startTimer();
      } else {
        const message =
          typeof res.data === "string" ? res.data : JSON.stringify(res.data);
        addToast(getTextByKey(message || "mfa-code-sent-on-email-failed"), {
          appearance: "error",
        });
      }
    }
  };

  const toggleMfa = async (
    Code: string,
    EnableMFA: boolean,
    sessionId: string
  ) => {
    const payload = {
      SessionId: sessionId,
      Code,
      EnableMFA,
    };
    setIsLoading(true);
    const response = await apiRequest(
      "Users",
      "UpdateUserMFA",
      payload,
      "POST"
    );
    setIsLoading(false);

    if (response.status === "success") {
      addToast(
        getTextByKey(
          EnableMFA ? "mfa-enabled-successfully" : "mfa-disabled-successfully"
        ),
        {
          appearance: "success",
        }
      );
      closeModal();
      updateSettings({ EnableMFA });
    } else {
      const message = JSON.stringify(response.data).replaceAll('"', "");
      
      return getTextByKey(
        message || (EnableMFA ? "mfa-enable-failed" : "mfa-disable-failed")
      );
    }
  };

  const openMfaLoginModal = (
    onMFACodeCheck: (...props: unknown[]) => void,
    email: string
  ) => {
    const handleCloseModal = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      if (e.target === e.currentTarget) {
        closeModal();
      }
    };

    setModal({
      modal: ManageMfaModal,
      container: ({ children }: React.PropsWithChildren<{}>) => (
        <div className={styles.modalContainer} onClick={handleCloseModal}>
          {children}
        </div>
      ),
      props: { enableMfa: false, onMFACodeCheck, skipFirstStep: true, email },
    });
  };

  return {
    sendMfaCodeOnEmail,
    toggleMfa,
    code,
    setCode,
    isResendEnabled,
    resendTimeLeft: timLeft as number,
    isLoading,
    generateSessionId,
    openMfaLoginModal,
    startTimer,
    isMailSent,
  };
};

export default useMfa;
